"use client";
import React, { useEffect, useId, useState } from "react";
import { codeToHtml } from "shiki";
import clsx from "clsx";
import { useDarkMode } from "src/hooks/useDarkMode";
import { colorMap, getColor } from "src/components/ui/ColorText";

type PCodeBlock = {
  children?: React.ReactNode;
  className?: string;
  inline?: boolean;
};

export const CodeBlock = ({
  children,
  className: cls,
  inline = false,
}: PCodeBlock) => {
  const [highlightedCode, setHighlightedCode] = useState("");
  const { resolvedTheme } = useDarkMode();
  const language =
    (cls &&
      cls
        .split(" ")
        .find((x: string) => x?.trim()?.startsWith("language-"))
        ?.replace(/language-/, "")) ||
    "text";
  const id = useId();
  const ref = `code-id-${id}`;

  const onClick = (id: string) => () => {
    const code = document.getElementById(id);
    if (!code) return;
    try {
      navigator.clipboard
        .writeText(code.innerText)
        .then(() => console.log("copied"));
    } catch (e) {}
  };

  useEffect(() => {
    if (!children || typeof children !== "string") return;

    const highlightCode = async () => {
      try {
        // Map language aliases to Shiki language names
        const langMap: Record<string, string> = {
          "fin-fun": "javascript", // Custom language mapped to JavaScript
          js: "javascript",
          ts: "typescript",
          jsx: "jsx",
          tsx: "tsx",
          text: "plaintext",
        };

        const shikiLang = langMap[language] || language;
        const theme = resolvedTheme === "light" ? "github-light" : "github-dark";

        let html = await codeToHtml(children.trim(), {
          lang: shikiLang,
          theme: theme,
        });

        // Custom post-processing for fin-fun language
        if (language === "fin-fun") {
          // Assign different colors to each financial function keyword
          // to match the original Prism token-based highlighting
          const keywordColors: Record<string, string> = {
            ...colorMap,
            XIRR: "var(--fin-fun-fv, red)", // Original red for XIRR
          };

          Object.entries(keywordColors).forEach(([keyword, color]) => {
            const regex = new RegExp(`\\b${keyword}\\b`, "g");
            html = html.replace(
              regex,
              `<span style="color: ${color}">${keyword}</span>`,
            );
          });
        }

        setHighlightedCode(html);
      } catch (error) {
        console.warn("Failed to highlight code:", error);
        setHighlightedCode(`<pre><code>${children.trim()}</code></pre>`);
      }
    };

    highlightCode();
  }, [children, language, resolvedTheme]);

  if (!children || typeof children !== "string") {
    return children;
  }

  if (inline) {
    return (
      <code
        onClick={onClick(ref)}
        className={clsx(
          "not-prose text-sm font-mono no-whitespace-normalization",
          "inline bg-neutral-100 rounded-sm dark:bg-neutral-900 px-1 py-0.5",
          cls,
        )}
      >
        {children.trim()}
      </code>
    );
  }

  return (
    <div
      id={ref}
      onClick={onClick(ref)}
      className={clsx(
        "not-prose text-sm font-mono overflow-x-scroll no-whitespace-normalization",
        "shiki-wrapper",
        cls,
      )}
      dangerouslySetInnerHTML={{ __html: highlightedCode }}
    />
  );
};
